import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  createExternalCertificate,
  updateExternalCertificate,
  getExternalCertificate,
  downloadExternalCertificate,
} from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Upload, QrCode, FileCheck, Loader, CheckCircle, Copy, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const QR_POSITIONS = [
  { value: 'bottom-right', label: 'Esquina inferior derecha' },
  { value: 'bottom-left', label: 'Esquina inferior izquierda' },
  { value: 'custom', label: 'Coordenadas personalizadas (X, Y)' },
];

export default function UploadExternalCertificate() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { certId } = useParams(); // if present → edit mode
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Post-upload success state
  const [successData, setSuccessData] = useState(null); // { code, owner_name, id }

  // Form state
  const [file, setFile] = useState(null);
  const [ownerName, setOwnerName] = useState('');
  const [courseName, setCourseName] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [qrPosition, setQrPosition] = useState('bottom-right');
  const [qrX, setQrX] = useState('');
  const [qrY, setQrY] = useState('');
  const [qrZ, setQrZ] = useState('');
  const [textFont, setTextFont] = useState('sans');
  const [textSize, setTextSize] = useState('10');
  const [existingCode, setExistingCode] = useState(null);

  const isEdit = !!certId;

  useEffect(() => {
    if (authLoading) return; // wait for auth to load
    if (!user || !['superadmin', 'admin', 'professor'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    if (isEdit) {
      loadCert();
    }
  }, [user, authLoading, certId]);

  const loadCert = async () => {
    setLoading(true);
    try {
      const res = await getExternalCertificate(certId);
      const c = res.data;
      setOwnerName(c.owner_name || '');
      setCourseName(c.course_name || '');
      setIssueDate(c.issue_date || '');
      setQrPosition(c.qr_position || 'bottom-right');
      setQrX(c.qr_x != null ? String(c.qr_x) : '');
      setQrY(c.qr_y != null ? String(c.qr_y) : '');
      setQrZ(c.qr_z != null ? String(c.qr_z) : '');
      setTextFont(c.text_font || 'sans');
      setTextSize(c.text_size != null ? String(c.text_size) : '10');
      setExistingCode(c.code);
    } catch {
      toast.error('Error al cargar el certificado');
      navigate('/admin/certificates');
    } finally {
      setLoading(false);
    }
  };

  // Drag & drop handlers
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) validateAndSetFile(dropped);
  };

  const validateAndSetFile = (f) => {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(f.type)) {
      toast.error('Solo se permiten PDF, PNG, JPG o WebP');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('El archivo no debe superar 10MB');
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !file) {
      toast.error('Selecciona el archivo del certificado');
      return;
    }
    if (!ownerName.trim() || !courseName.trim()) {
      toast.error('El nombre del titular y del curso son obligatorios');
      return;
    }
    if (qrPosition === 'custom' && (!qrX || !qrY)) {
      toast.error('Para posición personalizada debes ingresar X e Y');
      return;
    }

    const fd = new FormData();
    if (file) fd.append('file', file);
    fd.append('owner_name', ownerName.trim());
    fd.append('course_name', courseName.trim());
    if (issueDate) fd.append('issue_date', issueDate);
    fd.append('qr_position', qrPosition);
    if (qrPosition === 'custom') {
      fd.append('qr_x', qrX);
      fd.append('qr_y', qrY);
    }
    if (qrZ) fd.append('qr_z', qrZ);
    fd.append('text_font', textFont);
    fd.append('text_size', textSize || '10');

    setSaving(true);
    try {
      if (isEdit) {
        await updateExternalCertificate(certId, fd);
        toast.success('Certificado actualizado correctamente');
        navigate('/admin/certificates');
      } else {
        const res = await createExternalCertificate(fd);
        // Show success screen with verification code and download
        setSuccessData({
          id: res.data.id,
          code: res.data.code,
          owner_name: res.data.owner_name,
          course_name: res.data.course_name,
        });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al procesar el certificado';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const copyCode = (code) => {
    const fallback = () => {
      const el = document.createElement('textarea');
      el.value = code;
      el.style.cssText = 'position:fixed;top:-9999px;left:-9999px';
      document.body.appendChild(el);
      el.focus();
      el.select();
      try {
        document.execCommand('copy');
        toast.success('Código copiado al portapapeles');
      } catch {
        toast.info(`Código: ${code}`);
      }
      document.body.removeChild(el);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code)
        .then(() => toast.success('Código copiado al portapapeles'))
        .catch(fallback);
    } else {
      fallback();
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-6 h-6 animate-spin mr-2" />
        <span>Cargando...</span>
      </div>
    );
  }

  // ── Success screen after upload ────────────────────────────────────────────
  if (successData) {
    const verifyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/certificates/external/verify/${successData.code}`;
    const downloadUrl = downloadExternalCertificate(successData.id);
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center p-6">
        <div className="max-w-lg w-full">
          <Card className="shadow-xl">
            <CardContent className="pt-8 pb-8 text-center space-y-6">
              {/* Success icon */}
              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-primary">¡Certificado Creado!</h2>
                <p className="text-muted-foreground mt-1">El QR de verificación fue añadido al documento.</p>
              </div>

              {/* Certificate info */}
              <div className="bg-muted rounded-lg p-4 text-left space-y-2">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Titular</p>
                  <p className="font-semibold">{successData.owner_name}</p>
                </div>
                {successData.course_name && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Curso</p>
                    <p className="font-semibold">{successData.course_name}</p>
                  </div>
                )}
              </div>

              {/* Verification code */}
              <div className="border-2 border-primary/30 rounded-xl p-5 bg-primary/5">
                <p className="text-sm text-muted-foreground mb-2 font-medium">Número de Verificación</p>
                <div className="flex items-center justify-center gap-3">
                  <span
                    className="text-2xl font-mono font-bold tracking-widest text-primary"
                    data-testid="success-cert-code"
                  >
                    {successData.code}
                  </span>
                  <button
                    onClick={() => copyCode(successData.code)}
                    className="p-2 rounded-lg hover:bg-primary/10 transition-colors text-primary"
                    title="Copiar código"
                    data-testid="copy-code-btn"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  El participante puede verificar su certificado con este código en la página pública.
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                  data-testid="download-cert-btn"
                >
                  <Button className="w-full" size="lg">
                    <Download className="w-5 h-5 mr-2" />
                    Descargar Certificado con QR
                  </Button>
                </a>
                <a
                  href={`/verify`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button variant="outline" className="w-full" size="sm">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Verificar en página pública
                  </Button>
                </a>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => navigate('/admin/certificates?tab=external')}
                  data-testid="back-to-list-btn"
                >
                  Ver todos los certificados
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary p-6 md:p-12" data-testid="upload-external-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/admin/certificates')} className="mb-4 -ml-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Gestionar Certificados
          </Button>
          <h1 className="text-3xl font-heading font-bold text-primary">
            {isEdit ? 'Editar Certificado Externo' : 'Subir Certificado Externo'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEdit
              ? 'Modifica los datos y/o la posición del QR. Si cambias la posición, el certificado se reprocesará automáticamente.'
              : 'Sube un certificado existente (PDF o imagen) y el sistema añadirá automáticamente un código QR de verificación.'}
          </p>
          {isEdit && existingCode && (
            <p className="text-xs font-mono bg-muted px-2 py-1 rounded mt-2 inline-block">
              Código: {existingCode}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4">
                <QrCode className="w-5 h-5 text-primary" />
                <h2 className="font-semibold">
                  {isEdit ? 'Archivo del Certificado (opcional — sube uno nuevo para reemplazar)' : 'Archivo del Certificado *'}
                </h2>
              </div>

              <div
                ref={dropRef}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/40'}`}
                data-testid="drop-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={(e) => { if (e.target.files?.[0]) validateAndSetFile(e.target.files[0]); }}
                />
                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <FileCheck className="w-10 h-10 text-green-600" />
                    <p className="font-medium text-green-700">{file.name}</p>
                    <p className="text-sm text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    <Button type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                      Cambiar archivo
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Upload className="w-10 h-10" />
                    <p className="font-medium">Arrastra tu certificado aquí</p>
                    <p className="text-sm">o haz clic para seleccionar</p>
                    <p className="text-xs mt-1">Formatos: PDF, PNG, JPG, WEBP (máx. 10MB)</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <h2 className="font-semibold">Datos del Certificado</h2>

              <div className="space-y-2">
                <Label htmlFor="owner-name">Nombre del Titular *</Label>
                <Input
                  id="owner-name"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  placeholder="Ej. Juan Carlos Pérez"
                  required
                  data-testid="owner-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="course-name">Nombre del Curso/Programa *</Label>
                <Input
                  id="course-name"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="Ej. Diplomado en Seguridad Ejecutiva"
                  required
                  data-testid="course-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="issue-date">Fecha de Emisión (opcional)</Label>
                <Input
                  id="issue-date"
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  data-testid="issue-date-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* QR Position */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <QrCode className="w-5 h-5 text-primary" />
                <h2 className="font-semibold">Posición del Código QR</h2>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">
                El QR de verificación se añadirá en la posición que elijas. Bajo el QR se imprimirán "Verificar:" y el código en 2 líneas.
              </p>

              <div className="space-y-3">
                {QR_POSITIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border hover:bg-muted/50 transition-colors" data-testid={`qr-pos-${opt.value}`}>
                    <input
                      type="radio"
                      name="qr_position"
                      value={opt.value}
                      checked={qrPosition === opt.value}
                      onChange={(e) => setQrPosition(e.target.value)}
                      className="accent-primary"
                    />
                    <span className="text-sm font-medium">{opt.label}</span>
                  </label>
                ))}
              </div>

              {qrPosition === 'custom' && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  <div className="space-y-2">
                    <Label>X — píxeles desde la izquierda</Label>
                    <Input
                      type="number"
                      min="0"
                      value={qrX}
                      onChange={(e) => setQrX(e.target.value)}
                      placeholder="ej. 800"
                      required={qrPosition === 'custom'}
                      data-testid="qr-x-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Y — píxeles desde arriba</Label>
                    <Input
                      type="number"
                      min="0"
                      value={qrY}
                      onChange={(e) => setQrY(e.target.value)}
                      placeholder="ej. 600"
                      required={qrPosition === 'custom'}
                      data-testid="qr-y-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2 pt-2 border-t">
                <Label>Tamaño del QR (Z) — píxeles</Label>
                <Input
                  type="number"
                  min="40"
                  max="400"
                  value={qrZ}
                  onChange={(e) => setQrZ(e.target.value)}
                  placeholder="Por defecto: 150"
                  data-testid="qr-z-input"
                />
                <p className="text-xs text-muted-foreground">Recomendado: 120–200 px. Deja en blanco para usar 150 px.</p>
              </div>

              {/* Font controls for "Verificar" text */}
              <div className="pt-2 border-t space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Texto "Verificar"</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="text-font">Tipo de letra</Label>
                    <select
                      id="text-font"
                      value={textFont}
                      onChange={(e) => setTextFont(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      data-testid="text-font-select"
                    >
                      <option value="sans">Sans-serif (Arial)</option>
                      <option value="serif">Serif (Times New Roman)</option>
                      <option value="mono">Monoespaciada (Courier)</option>
                      <option value="dejavu">DejaVu Sans</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="text-size">Tamaño de letra (pt)</Label>
                    <Input
                      id="text-size"
                      type="number"
                      min="6"
                      max="36"
                      value={textSize}
                      onChange={(e) => setTextSize(e.target.value)}
                      placeholder="Por defecto: 10"
                      data-testid="text-size-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Controla la fuente y el tamaño de la etiqueta "Verificar:" y el código que aparecen bajo el QR.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={saving}
              className="flex-1"
              data-testid="submit-external-btn"
            >
              {saving ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  {isEdit ? 'Guardando...' : 'Procesando certificado...'}
                </>
              ) : (
                <>
                  {isEdit ? 'Guardar Cambios' : 'Subir y Generar QR'}
                </>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/admin/certificates')}>
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
